import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Button, Input, Segmented, Select, Spin, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  fetchSprCards,
  fetchSprCardVids
} from "./spr-cards-api";
import SprCardDrawer from "./spr-card-drawer";
import SprCardsRegisterDrawer from "./spr-cards-register-drawer";
import { cardColorCss, DOC_STATUS_LABELS } from "@core/card-keys";
import { getLawFlag } from "../status/spr-status-utils";
import "./spr-cards.css";

const STATES = [
  { value: "all", label: "Все" },
  { value: "free", label: "Свободные" },
  { value: "issued", label: "Выданные" },
  { value: "blocked", label: "Заблокированные" }
];

const SprCards = () => {
  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 46) === 1;

  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [vid, setVid] = useState();
  const [vids, setVids] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  const loadVids = useCallback(async () => {
    try {
      const data = await fetchSprCardVids();
      setVids(Array.isArray(data) ? data : []);
    } catch {
      setVids([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSprCards({ q, vid, state });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, vid, state]);

  useEffect(() => { loadVids(); }, [loadVids]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const statusTag = (r) => {
    if (Number(r.isDel) === 1) return <Tag color="red">Заблокирована</Tag>;
    if (Number(r.idDocExp) || Number(r.idClient)) {
      return <Tag color="green">Выдана</Tag>;
    }
    return <Tag>Свободна</Tag>;
  };

  const columns = useMemo(() => [
    { title: "Код", dataIndex: "keyCard", width: 130, render: (v) => <code>{v}</code> },
    { title: "Номер", dataIndex: "keyCardShort", width: 100, render: (v) => v || "—" },
    {
      title: "Тип",
      dataIndex: "vidName",
      width: 150,
      render: (v, r) => {
        const css = cardColorCss(r.color);
        return (
          <span className="spr-cards-vid">
            <span className="spr-cards-vid-dot" style={{ background: css || "#fff" }} />
            {v || "—"}
          </span>
        );
      }
    },
    { title: "Статус", width: 220, render: (_, r) => statusTag(r) },
    {
      title: "Выдана кому",
      render: (_, r) => {
        if (!r.holderName) return "—";
        const st = DOC_STATUS_LABELS[String(r.holderDocStatus)];
        return <span>{r.holderName}{st ? ` · ${st}` : ""}</span>;
      }
    },
    {
      title: "Дата регистрации",
      dataIndex: "dateReg",
      width: 130,
      render: (v) => (v && dayjs(v).isValid() ? dayjs(v).format("DD.MM.YYYY") : "—")
    }
  ], []);

  return (
    <div className="spr-cards-page">
      <div className="spr-cards-toolbar">
        {canEdit ? (
          <Tooltip title="Зарегистрировать карты">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setRegisterOpen(true)} />
          </Tooltip>
        ) : null}
        <Input
          className="spr-cards-search"
          placeholder="Код, номер или имя владельца"
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Segmented value={state} options={STATES} onChange={setState} />
        <Select
          className="spr-cards-vid-select"
          placeholder="Все типы"
          allowClear
          value={vid}
          onChange={setVid}
          options={vids.map((x) => ({ value: x.vid, label: x.name }))}
        />
      </div>
      <div className="spr-cards-body">
        <Spin spinning={loading}>
          <Table
            rowKey="idCard"
            size="middle"
            columns={columns}
            dataSource={items}
            pagination={false}
            locale={{ emptyText: "Карт нет" }}
            onRow={(record) => ({
              className: "spr-cards-row",
              onClick: () => setCard(record)
            })}
          />
        </Spin>
      </div>
      <SprCardDrawer
        card={card}
        vids={vids}
        open={Boolean(card)}
        canEdit={canEdit}
        onClose={() => setCard(null)}
        onSaved={load}
      />
      <SprCardsRegisterDrawer
        vids={vids}
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSaved={load}
      />
    </div>
  );
};

export default SprCards;
